import * as THREE from 'three'
import * as d3 from 'd3'

import { getPoint } from './poly'

const time_delta = 1 / 100

const opacity_scale = d3.scaleSqrt().domain([0, 10000]).range([0.9, 0.3])

const geometry = new THREE.SphereGeometry(0.121, 10, 10)

const dullColor = '#dddddd'
const dullHex = 0xdddddd
const pbwpColor = '#f4511e'
const pbwpHex = 0xf4511e
const highlightColor = '#ffff00'
const highlightHex = 0xffff00

const fadeTime = 250

const pbwpScale = d3.interpolateLab(highlightColor, pbwpColor)
const dullScale = d3.interpolateLab(highlightColor, dullColor)

export default class Pitch {
    constructor(data, initial_time, size, show = false) {
        this.data = data

        this.initial_time = initial_time
        this.frame = 0
        this.show = show

        this.color = data.is_runner_pbwp ? pbwpHex : dullHex
        this.opacity = data.is_runner_pbwp ? 1 : opacity_scale(size)
        this.opacityScale = d3
            .scaleLinear()
            .domain([0, fadeTime])
            .range([1, this.opacity])

        this.highlightScale = data.is_runner_pbwp ? pbwpScale : dullScale

        this.createObject()
    }

    createObject() {
        const material = new THREE.MeshBasicMaterial({ color: this.color })
        // Have switched this back and forth several times.
        material.transparent = true
        material.opacity = this.opacity
        const sphere = new THREE.Mesh(geometry, material)

        this.object = sphere
        this.object.visible = this.show
        this.object.name = 'pitch'

        this.object.userData.data = this.data
        this.object.userData.done = !this.show
    }

    hide() {
        this.show = false
        this.object.visible = false
    }

    show() {
        this.reset()
    }

    reset() {
        this.show = true
        this.object.visible = true
        this.frame = 0
        this.object.userData.done = false
    }

    draw() {
        if (this.fadeHighlight) {
            const time = new Date().getTime()
            const diff = time - this.mouseoutTime

            const color = d3.color(
                this.highlightScale(Math.min(diff / fadeTime, 1))
            )
            const htmlHex = color.formatHex()

            const hex = parseInt(`0x${htmlHex.slice(1)}`, 16)

            this.object.material.color.setHex(hex)
            if (!this.data.is_runner_pbwp) {
                this.object.material.opacity = this.opacityScale(diff)
            }

            if (diff > fadeTime) {
                this.object.material.color.setHex(this.color)
                this.object.material.opacity = this.opacity
                this.fadeHighlight = false
            }
        }

        if (this.object.userData.done) {
            return
        }

        const time =
            this.data.api_plate_time -
            0.1 -
            this.initial_time +
            this.frame * time_delta

        const [x, y, z] = [
            getPoint(this.data.poly_x, time),
            getPoint(this.data.poly_y, time),
            getPoint(this.data.poly_z, time),
        ]

        if (y >= 12.5) {
            this.object.material.opacity = 0
        } else if (z <= 0 || y <= -2.5) {
            this.object.material.opacity = this.opacity

            this.object.position.x = this.data.location_x
            this.object.position.y = this.data.location_y
            this.object.position.z = this.data.location_z

            this.object.userData.done = true
        } else {
            this.object.material.opacity = 1
            this.object.position.x = x
            this.object.position.y = y
            this.object.position.z = z
        }

        this.frame += 1
    }

    highlight() {
        this.fadeHighlight = false

        this.object.material.color.setHex(highlightHex)
        this.object.material.opacity = 1

        if (this.timeout) {
            clearTimeout(this.timeout)
        }

        this.timeout = setTimeout(() => {
            this.mouseoutTime = new Date().getTime()
            this.fadeHighlight = true
        }, 17)
    }

    openVideo() {
        const video_url = `/sporty-videos?playId=${this.data.play_id}`

        window.open(video_url, '_blank')
    }
}
